import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  X, Search, Filter, FolderOpen, AlertCircle,
  Plane, Key, Wrench, Compass
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useAppContext } from "@/context/AppContext";
import AircraftProductCard from "./AircraftProductCard";
import AircraftRentalCard from "./AircraftRentalCard";
import AircraftPartCard from "./AircraftPartCard";
import ExperienceCard from "./ExperienceCard";

interface SearchOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  initialQuery?: string;
}

const SearchOverlay = ({ isOpen, onClose, initialQuery = "" }: SearchOverlayProps) => {
  const { getDictValue } = useAuth();
  const { aircraftListings, aircraftRentals, aircraftParts, experiences } = useAppContext();

  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [activeCategory, setActiveCategory] = useState<"ALL" | "SALE" | "RENTAL" | "PART" | "EXPERIENCE">("ALL");

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setSearchQuery(initialQuery);
      setTimeout(() => inputRef.current?.focus(), 100);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen, initialQuery]);

  // 🟢 DEEP SEARCH RECURSIV
  const rawResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const query = searchQuery.toLowerCase();

    const matchesAnyField = (obj: any): boolean => {
      if (obj === null || obj === undefined) return false;
      if (typeof obj === "string") return obj.toLowerCase().includes(query);
      if (typeof obj === "number" || typeof obj === "boolean") return String(obj).toLowerCase().includes(query);
      if (Array.isArray(obj)) return obj.some(item => matchesAnyField(item));
      if (typeof obj === "object") return Object.values(obj).some(val => matchesAnyField(val));
      return false;
    };

    const matchedSales = aircraftListings
      .filter(item => item.verified && matchesAnyField(item))
      .map(item => ({ ...item, cardType: "SALE" as const }));

    const matchedRentals = aircraftRentals
      .filter(item => item.verified && matchesAnyField(item))
      .map(item => ({ ...item, cardType: "RENTAL" as const }));

    const matchedParts = aircraftParts
      .filter(item => item.verified && matchesAnyField(item))
      .map(item => ({ ...item, cardType: "PART" as const }));

    const matchedExperiences = experiences
      .filter(item => item.verified && matchesAnyField(item))
      .map(item => ({ ...item, cardType: "EXPERIENCE" as const }));

    return [...matchedSales, ...matchedRentals, ...matchedParts, ...matchedExperiences];
  }, [searchQuery, aircraftListings, aircraftRentals, aircraftParts, experiences]);

  // Filtrarea după tab-ul selectat din sidebar-ul stâng
  const filteredResults = useMemo(() => {
    if (activeCategory === "ALL") return rawResults;
    return rawResults.filter(item => item.cardType === activeCategory);
  }, [rawResults, activeCategory]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-[#020617]/70 backdrop-blur-md p-4 md:p-6 animate-in fade-in duration-200">

      {/* Containerul Modalului Principal (Supradimensionat pe 2 coloane) */}
      <div className="relative w-full max-w-7xl h-[90vh] bg-[#070b15]/95 border border-slate-800/80 rounded-[2rem] shadow-2xl flex overflow-hidden animate-in zoom-in-95 duration-300">

        {/* ======================================================================== */}
        {/* 1. SIDEBAR STÂNGA: FILTRE                                                */}
        {/* ======================================================================== */}
        <div className="w-80 border-r border-slate-800/50 bg-slate-950/40 p-8 flex flex-col shrink-0 hidden lg:flex">
          <div className="space-y-8">
            <div className="flex items-center gap-3.5">
              <div className="w-10 h-10 bg-blue-600/10 border border-blue-500/20 rounded-xl flex items-center justify-center text-blue-400">
                <Filter size={18} />
              </div>
              <div>
                <p className="text-[10px] text-slate-500 uppercase font-mono tracking-widest font-bold">Configurare</p>
                <h3 className="text-sm font-black text-white uppercase tracking-wider">Filtre Căutare</h3>
              </div>
            </div>

            {/* Categorii de Căutare */}
            <div className="space-y-2">
              <span className="text-[9px] font-bold text-slate-500 uppercase tracking-widest mb-3 block">Filtrează după Departament</span>
              {[
                { code: "ALL", label: "Toate Departamentele", icon: FolderOpen, color: "text-blue-400" },
                { code: "SALE", label: "Aeronave Vânzare", icon: Plane, color: "text-orange-400" },
                { code: "RENTAL", label: "Flotă Închirieri", icon: Key, color: "text-blue-400" },
                { code: "PART", label: "Piese de Schimb", icon: Wrench, color: "text-emerald-400" },
                { code: "EXPERIENCE", label: "Experiențe Aviatice", icon: Compass, color: "text-red-400" },
              ].map((cat) => {
                const Icon = cat.icon;
                const isSelected = activeCategory === cat.code;
                return (
                  <button
                    key={cat.code}
                    onClick={() => setActiveCategory(cat.code as any)}
                    className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border text-left transition-all text-xs font-bold uppercase tracking-wider ${
                      isSelected
                        ? "bg-blue-600/10 border-blue-500/30 text-white shadow-inner"
                        : "bg-slate-900/10 border-transparent text-slate-400 hover:text-white hover:bg-white/[0.02]"
                    }`}
                  >
                    <Icon size={14} className={isSelected ? cat.color : "text-slate-500"} />
                    <span className="flex-1 truncate">{cat.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* ======================================================================== */}
        {/* 2. CORP CENTRAL (ACUM COMPLET EXTINS): BARĂ CĂUTARE & GRID CARDURI        */}
        {/* ======================================================================== */}
        <div className="flex-1 flex flex-col bg-transparent">

          {/* Bară de Căutare de Sus cu butonul X integrat */}
          <div className="px-8 pt-8 pb-4 shrink-0 flex items-center gap-4">
            <div className="relative flex-1">
              <div className="relative flex items-center bg-[#0F172A]/80 border border-slate-800 rounded-2xl h-14 px-4 focus-within:border-blue-500/40 focus-within:shadow-[0_0_20px_rgba(14,165,233,0.1)] transition-all duration-300">
                <Search className="h-5 w-5 text-slate-500 shrink-0 mr-3 animate-pulse" />
                <input
                  ref={inputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={getDictValue("home.search.placeholder") || "Caută în sistem..."}
                  className="w-full h-full bg-transparent text-sm text-white border-0 outline-none focus:ring-0 placeholder:text-slate-500 font-sans"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="p-1 text-slate-500 hover:text-white rounded-lg hover:bg-white/5 transition-all"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Buton Închidere Modal (Înlocuiește butonul din sidebar-ul șters) */}
            <button
              onClick={onClose}
              className="h-14 w-14 bg-[#0F172A]/80 border border-slate-800 rounded-2xl flex items-center justify-center text-slate-400 hover:text-white hover:border-slate-700 active:scale-95 transition-all shrink-0"
              title="Închide căutarea"
            >
              <X size={20} />
            </button>
          </div>

          {/* Zonă de Scroll rezultate */}
          <div className="flex-1 overflow-y-auto px-8 pb-8 custom-scrollbar">
            {searchQuery.trim() === "" ? (
              <div className="h-full flex flex-col items-center justify-center text-center space-y-4 py-16">
                <div className="w-14 h-14 bg-blue-600/10 border border-blue-500/20 rounded-2xl flex items-center justify-center text-blue-400">
                  <Search size={24} className="animate-bounce" />
                </div>
                <div>
                  <h3 className="text-sm font-black text-white uppercase tracking-wider">Terminal Tranzacții Activ</h3>
                  <p className="text-xs text-slate-500 font-mono mt-1">Introduceți termenii de căutare pentru scanarea hangarului virtual.</p>
                </div>
              </div>
            ) : filteredResults.length > 0 ? (
              <div className="space-y-6 mt-4">
                {/* Header de număr de rezultate */}
                <div className="flex items-center gap-2 font-mono text-xs text-slate-500 border-b border-slate-800/50 pb-3">
                  <FolderOpen size={14} className="text-blue-500" />
                  <span className="uppercase tracking-wider">
                    {filteredResults.length} {getDictValue("home.search.results")} pentru "{searchQuery}"
                  </span>
                </div>

                {/* Grid de Carduri */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredResults.map((item, index) => {
                    switch (item.cardType) {
                      case "SALE":
                        return <AircraftProductCard key={`sale-${item.listingId || index}`} listing={item} />;
                      case "RENTAL":
                        return <AircraftRentalCard key={`rental-${item.rentalId || index}`} rental={item} />;
                      case "PART":
                        return <AircraftPartCard key={`part-${item.partId || index}`} part={item} />;
                      case "EXPERIENCE":
                        return <ExperienceCard key={`exp-${item.experienceId || index}`} experience={item} />;
                      default:
                        return null;
                    }
                  })}
                </div>
              </div>
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-center space-y-4 py-16">
                <div className="w-14 h-14 bg-red-500/10 border border-red-500/20 rounded-2xl flex items-center justify-center text-red-400">
                  <AlertCircle size={24} />
                </div>
                <div>
                  <h3 className="text-sm font-black text-white uppercase tracking-wider">
                    {getDictValue("home.search.noResults") || "Căutarea nu a returnat date"}
                  </h3>
                  <p className="text-xs text-slate-500 font-mono mt-1">Nu există matching-uri conform parametrilor setați în sidebar.</p>
                </div>
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default SearchOverlay;